import { AddRounded, AddTask, AssignmentIndOutlined, GroupsOutlined } from "@mui/icons-material";
import {
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  styled,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  CreateIssueModal,
  CreateRoleModal,
  CreateTaskItemModal,
  CreateTeamModal,
  MaterialSymbolIcon,
  MaterialSymbols,
} from "..";
import { Links } from "../../constants";
import { useSession, useSettings } from "../../hooks";

const IconButtonText = styled(Typography)<{ $isExpanded: boolean }>`
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  margin-left: ${({ $isExpanded }) => ($isExpanded ? "5px" : 0)};
  overflow: hidden;
  white-space: nowrap;
  font-size: 0.875rem;
  transition: ${({ theme }) => theme.transitions.create(["opacity", "margin"])};
  text-transform: uppercase;
`;

const SideMenuAddButton = () => {
  const { isSessionLoaded } = useSession();
  const { sideMenuSettings } = useSettings();
  const navigate = useNavigate();
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [isCreateIssueModalOpen, setIsCreateIssueModalOpen] = useState(false);
  const [isCreateTaskItemModalOpen, setIsCreateTaskItemModalOpen] = useState(false);
  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false);
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);

  function handleTeamCreated(slug: string) {
    setIsCreateTeamModalOpen(false);
    navigate(Links.Team(slug));
  }

  function handleRoleCreated(guid: string) {
    setIsCreateRoleModalOpen(false);
    navigate(Links.RoleDetail(guid));
  }

  return (
    <>
      <ListItemButton
        disabled={!isSessionLoaded}
        disableRipple
        onClick={(e) => setAddMenuAnchor(e.currentTarget)}
        sx={{ p: 0, width: "100%", "&:hover": { backgroundColor: "transparent" } }}
      >
        <IconButton variant="filled" disabled={!isSessionLoaded} sx={{ mr: 0.5 }}>
          <AddRounded fontSize="large" />
        </IconButton>
        <IconButtonText $isExpanded={sideMenuSettings.isExpanded}>Add</IconButtonText>
      </ListItemButton>

      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
        sx={{ m: 0.5 }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 180,
            },
          },
        }}
        anchorOrigin={
          sideMenuSettings.isExpanded
            ? { vertical: "top", horizontal: "right" }
            : { vertical: "bottom", horizontal: "right" }
        }
        transformOrigin={
          sideMenuSettings.isExpanded
            ? { vertical: "bottom", horizontal: "left" }
            : { vertical: "bottom", horizontal: "left" }
        }
      >
        <MenuItem
          onClick={() => {
            setAddMenuAnchor(null);
            setIsCreateTaskItemModalOpen(true);
          }}
        >
          <ListItemIcon>
            <AddTask />
          </ListItemIcon>
          <ListItemText>Task</ListItemText>
        </MenuItem>
        <MenuItem
          divider
          onClick={() => {
            setAddMenuAnchor(null);
            setIsCreateIssueModalOpen(true);
          }}
        >
          <ListItemIcon>
            <MaterialSymbolIcon symbol={MaterialSymbols.QuickPhrases} />
          </ListItemIcon>
          <ListItemText>Loop</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAddMenuAnchor(null);
            setIsCreateTeamModalOpen(true);
          }}
        >
          <ListItemIcon>
            <GroupsOutlined />
          </ListItemIcon>
          <ListItemText>Team</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAddMenuAnchor(null);
            setIsCreateRoleModalOpen(true);
          }}
        >
          <ListItemIcon>
            <AssignmentIndOutlined />
          </ListItemIcon>
          <ListItemText>Role</ListItemText>
        </MenuItem>
      </Menu>

      <CreateIssueModal
        visible={isCreateIssueModalOpen}
        onCreated={() => setIsCreateIssueModalOpen(false)}
        onCancelled={() => setIsCreateIssueModalOpen(false)}
      />

      <CreateTaskItemModal
        visible={isCreateTaskItemModalOpen}
        onCreated={() => setIsCreateTaskItemModalOpen(false)}
        onCancelled={() => setIsCreateTaskItemModalOpen(false)}
      />

      <CreateTeamModal
        visible={isCreateTeamModalOpen}
        onCreated={handleTeamCreated}
        onCancel={() => setIsCreateTeamModalOpen(false)}
      />

      <CreateRoleModal
        visible={isCreateRoleModalOpen}
        onCreated={handleRoleCreated}
        onCancelled={() => setIsCreateRoleModalOpen(false)}
      />
    </>
  );
};

export { SideMenuAddButton };
